/**
 * Full-page Applicant Review — replaces the overlay modal in
 * RecruitmentCommandCenter.jsx. Mounted at /recruitment/applicants/:applicantId.
 *
 * Iter137 — Back-button logout fix.
 *   The reported "clicking Back logs me out" symptom was caused by two
 *   subtle issues:
 *     1. `onClose={goBack}` meant that the drawer's own close handlers —
 *        including the one that runs AFTER delete-applicant — would
 *        navigate away from this page.  If any lingering API error
 *        bubbled during that transition it looked like a logout.
 *     2. `navigate('/recruitment')` hard-replaces the route; when the
 *        browser's history stack is empty (e.g. deep-link into this
 *        page) a fresh route evaluation plus a transient auth check
 *        could redirect to /login.
 *   Fix: swallow drawer-close navigations (we're a full page, not a
 *   drawer), prefer `navigate(-1)` so the user ends up exactly where
 *   they came from, and keep the fallback for direct hits.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import ApplicantDetailDrawer from './recruitment/ApplicantDetailDrawer';

export default function ApplicantReviewPage() {
  const { applicantId } = useParams();
  const navigate = useNavigate();

  const goBack = React.useCallback(() => {
    // Prefer the real previous entry so cancellations don't trigger a
    // fresh /recruitment mount with a half-loaded auth state.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/recruitment', { replace: true });
    }
  }, [navigate]);

  // The drawer uses `onClose` for its own internal close actions (e.g.
  // after a delete).  On this full-page screen we don't want those to
  // navigate anywhere — the user owns navigation via the Back button.
  const noopClose = React.useCallback(() => {}, []);
  const afterDelete = React.useCallback(() => {
    // After a successful delete, THEN go back.
    goBack();
  }, [goBack]);

  return (
    <div className="space-y-4" data-testid="applicant-review-page">
      <div className="flex items-center gap-3">
        <Button
          onClick={goBack}
          variant="outline"
          className="border-slate-600 !text-slate-200"
          data-testid="applicant-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Recruitment
        </Button>
        <div>
          <h1 className="text-xl font-bold text-white">Applicant Review</h1>
          <p className="text-[11px] text-slate-400 font-mono">{applicantId}</p>
        </div>
      </div>
      <div className="bg-[#0B1220] border border-slate-700 rounded-lg overflow-hidden">
        <ApplicantDetailDrawer
          applicantId={applicantId}
          onClose={noopClose}
          onAfterChange={afterDelete}
        />
      </div>
    </div>
  );
}
